/* eslint-disable @typescript-eslint/no-explicit-any */
import { useRef } from "react";
import { Device } from "@twilio/voice-sdk";
import { RootState } from "../redux/store";
import { useDispatch, useSelector } from "react-redux";
import {
  setCallStates,
  setMinimizeCall,
  setSearchValue,
} from "../redux/slices/twilioCallingSlice";
import { customToast } from "../utils/custom-toast/custom-toast";
import { useGetVerbaCallTokenMutation } from "../redux/services/communicationApi";

export const useTwilioCallHandler = (
  leadId?: string,
  leadType?: string,
  leadDetails?: any,
  dialerNumber?: string,
  isLeadListing?: boolean
) => {
  const dispatch = useDispatch();
  const twilioCalling = useSelector((state: RootState) => state.twilioCalling);
  const [getVerbaCallToken] = useGetVerbaCallTokenMutation();
  const userDetails = useSelector((state: RootState) => state?.auth?.user);
  const timerIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const callingNumber =
    dialerNumber && dialerNumber?.length > 0
      ? dialerNumber
      : `${leadDetails?.lead?.country_code ?? ""}${
          leadDetails?.lead?.phone ?? ""
        }`;

  const cleanupAfterCall = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    if (twilioCalling?.callStates?.timerInterval) {
      clearInterval(twilioCalling?.callStates?.timerInterval);
    }

    dispatch(
      setCallStates({
        callingNumber: "",
        isCalling: false,
        isMuted: false,
        callStatus: "",
        activeConnection: {},
        callDuration: 0,
        timerInterval: null,
        redirectTo: "",
        device: null,
        callingLeadId: "",
      })
    );
    dispatch(setSearchValue(""));
  };

  const handleMakeCall = async () => {
    if (!callingNumber) {
      customToast("Phone Number is not available", "error");
      return;
    }

    try {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }

      console.log("Generating new call token");
      const { data } = await getVerbaCallToken({
        leadId: leadId ?? "",
        leadType: leadType ?? "",
        leadListing: isLeadListing ?? false,
      });
      console.log("Call token generated", data);
      const twilioDevice = new Device(data.token);

      dispatch(
        setCallStates({
          device: twilioDevice,
          callingLeadId: leadId,
          callingNumber,
          isCalling: true,
          callStatus: "Calling",
          redirectTo: window.location.pathname,
        })
      );

      const connection = await twilioDevice.connect({
        params: {
          To: callingNumber,
          agent_id: userDetails?._id ?? "",
          agent_name: userDetails?.name ?? "",
          ...(leadId
            ? leadType === "anonymous"
              ? { anonymous_lead_id: leadId }
              : { lead_id: leadId }
            : {}),
        },
      });

      dispatch(
        setCallStates({
          activeConnection: connection,
        })
      );

      if (connection && typeof connection.on === "function") {
        connection.on("ringing", () => {
          dispatch(setCallStates({ callStatus: "Ringing" }));
        });

        connection.on("accept", () => {
          if (connection.status() === "open") {
            if (timerIntervalRef.current) {
              clearInterval(timerIntervalRef.current);
            }

            let duration = 0;
            const interval = setInterval(() => {
              duration += 1;
              dispatch(setCallStates({ callDuration: duration }));
            }, 1000);

            timerIntervalRef.current = interval;

            dispatch(
              setCallStates({
                callStatus: "Connected",
                isMuted: false,
                callDuration: 0,
                timerInterval: interval,
              })
            );
          }
        });

        connection.on("disconnect", () => {
          cleanupAfterCall();
          customToast("The call has ended", "error");
          dispatch(setMinimizeCall(false));
        });

        connection.on("reject", () => {
          cleanupAfterCall();
        });

        connection.on("error", () => {
          cleanupAfterCall();
        });
      }
    } catch {
      customToast("Something went wrong", "error");
      cleanupAfterCall();
    }
  };

  const handleHangUp = () => {
    if (twilioCalling?.callStates?.activeConnection) {
      twilioCalling?.callStates?.activeConnection?.disconnect();
    } else if (twilioCalling?.callStates?.device) {
      twilioCalling?.callStates?.device?.disconnectAll();
    }
    dispatch(setMinimizeCall(false));
  };

  const handleToggleMute = () => {
    if (twilioCalling?.callStates?.activeConnection) {
      if (twilioCalling?.callStates?.isMuted) {
        twilioCalling?.callStates?.activeConnection?.mute(false);
        dispatch(setCallStates({ isMuted: false }));
      } else {
        twilioCalling?.callStates?.activeConnection?.mute(true);
        dispatch(setCallStates({ isMuted: true }));
      }
    }
  };

  return {
    handleMakeCall,
    handleHangUp,
    handleToggleMute,
  };
};
